import React, { useState, useEffect } from "react";
import {
  getSubscriptionStatus,
  createPortalSession,
  SubscriptionStatus
} from "../api/stripeSubscription";
import { Skeleton } from "./Skeleton";

const SubscriptionManager: React.FC = () => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const status = await getSubscriptionStatus();
      setSubscription(status);
    } catch (error) {
      console.error("Failed to load subscription status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscriptions = async () => {
    try {
      setPortalLoading(true);
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (error) {
      console.error("Failed to open billing portal:", error);
      alert("Failed to open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="eai-panel">
        <Skeleton className="h-5 w-48" radius="md" />
        <Skeleton className="mt-4 h-3.5 w-1/2" />
      </div>
    );
  }

  if (!subscription || subscription.status === 'no_subscription') {
    return (
      <div className="eai-panel">
        <h3 className="eai-panel-title">No Active Subscription</h3>
        <p className="eai-description-sm mt-1">
          You don't have an active subscription. Subscribe to unlock premium features.
        </p>
      </div>
    );
  }

  // Status is a state, not a fact, so it keeps its colour; everything else reads flat
  const statusTone =
    subscription.status === 'active'
      ? 'text-eai-success-text'
      : subscription.status === 'canceled'
      ? 'text-eai-danger-text'
      : 'text-eai-warning-text';

  return (
    <div className="eai-panel">
      <h3 className="eai-panel-title mb-4">Subscription Details</h3>

      <dl className="flex flex-col gap-3 font-inter text-eai-body-sm">
        <div className="flex justify-between gap-4">
          <dt className="text-eai-ink-subtle">Status</dt>
          <dd className={`font-medium ${statusTone}`}>
            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
          </dd>
        </div>

        {subscription.currentPeriodEnd && (
          <div className="flex justify-between gap-4">
            <dt className="text-eai-ink-subtle">
              {subscription.cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
            </dt>
            <dd className="font-medium text-eai-ink">{formatDate(subscription.currentPeriodEnd)}</dd>
          </div>
        )}

        {subscription.customerEmail && (
          <div className="flex justify-between gap-4">
            <dt className="text-eai-ink-subtle">Billing email</dt>
            <dd className="min-w-0 truncate font-medium text-eai-ink">{subscription.customerEmail}</dd>
          </div>
        )}
      </dl>

      {subscription.cancelAtPeriodEnd && (
        <div className="mt-4 rounded-eai-card border border-eai-border bg-eai-surface-sunken p-3">
          <p className="font-inter text-eai-body-sm text-eai-warning-text">
            Your subscription is set to cancel at the end of the billing period.
            You will retain access until {formatDate(subscription.currentPeriodEnd!)}.
          </p>
        </div>
      )}

      <div className="mt-5">
        <button
          onClick={handleManageSubscriptions}
          disabled={portalLoading}
          className="eai-btn-outline"
        >
          {portalLoading ? 'Loading…' : 'Manage Subscriptions'}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionManager;
